import { __count, action, atom, isAction, isAtom, throwReatomError } from "@reatom/core";
import { __thenReatomed, onCtxAbort } from "@reatom/effects";
import { onUpdate } from "@reatom/hooks";
import { assign, isRec, isShallowEqual, noop } from "@reatom/utils";
import { isLinkedListAtom } from "@reatom/primitives";

//#region src/utils.ts
const mapName = ({ __reatom: proto }, operator, name) => name ?? `${proto.name}.${operator}`;

//#endregion
//#region src/match.ts
function match(expression, name = __count("match")) {
	const cases = [];
	let _truthy;
	let _falsy;
	let _default;
	const matchAtom = atom((ctxSpy) => {
		const value = isAtom(expression) ? ctxSpy.spy(expression) : typeof expression === "function" ? expression(ctxSpy) : expression;
		const list = [
			...cases,
			_truthy,
			_falsy,
			_default
		].filter(Boolean);
		for (const { clause, statement } of list) if (clause(ctxSpy, value)) return isAtom(statement) ? ctxSpy.spy(statement) : typeof statement === "function" ? statement(ctxSpy, value) : statement;
		return undefined;
	}, name);
	return Object.assign(matchAtom, {
		is(clause, statement) {
			cases.push({
				clause: isAtom(clause) ? (ctx, value) => Object.is(value, ctx.spy(clause)) : typeof clause === "function" ? clause : (ctx, value) => Object.is(value, clause),
				statement
			});
			return matchAtom;
		},
		with(part, statement) {
			cases.push({
				clause: (ctx, expr) => {
					const visit = (part$1, expr$1) => {
						if (isRec(part$1)) {
							for (const key in part$1) if (!visit(part$1[key], expr$1[key])) return false;
							return true;
						}
						return Object.is(part$1, expr$1);
					};
					return visit(part, expr);
				},
				statement
			});
			return matchAtom;
		},
		truthy(statement) {
			throwReatomError(_truthy, "the case is already defined");
			_truthy = {
				clause: (ctx, value) => !!value,
				statement
			};
			return matchAtom;
		},
		falsy(statement) {
			throwReatomError(_falsy, "the case is already defined");
			_falsy = {
				clause: (ctx, value) => !value,
				statement
			};
			return matchAtom;
		},
		default(statement = () => throwReatomError(true, "no match")) {
			throwReatomError(_default, "the case is already defined");
			_default = {
				clause: (ctx, value) => true,
				statement
			};
			return matchAtom;
		}
	});
}

//#endregion
//#region src/bind.ts
const ctxMap = new WeakMap();
const bind = (ctx, fn) => {
	let fnMap = ctxMap.get(ctx);
	if (!fnMap) ctxMap.set(ctx, fnMap = new WeakMap());
	let bfn = fnMap.get(fn);
	if (!bfn) fnMap.set(fn, bfn = fn.bind(null, ctx));
	return bfn;
};

//#endregion
//#region src/delay.ts
const delay = (options, name) => (anAtom) => {
	const running = new WeakMap();
	let { min: minOption, max: maxOption, leading = true, trailing = true, abortable = true } = options;
	throwReatomError(minOption === undefined && maxOption === undefined, "wrong options");
	minOption ??= maxOption;
	maxOption ??= Number.MAX_SAFE_INTEGER;
	name = mapName(anAtom, "delay", name);
	const minAtom = isAtom(minOption) ? minOption : atom(minOption, `${name}._minAtom`);
	const maxAtom = isAtom(maxOption) ? maxOption : atom(maxOption, `${name}._maxAtom`);
	const theAtom = atom((ctx, prevState) => {
		const min = ctx.get(minAtom);
		const max = ctx.get(maxAtom);
		const startsKey = ctx.cause.listeners;
		const depState = ctx.spy(anAtom);
		let state = ctx.cause.pubs.length ? prevState : proto.isAction ? [] : depState;
		if (!ctx.cause.pubs.length) state = proto.isAction ? [] : depState;
		else {
			const now = Date.now();
			const isRunning = running.has(startsKey);
			const start = running.get(startsKey) ?? now;
			const skip = isRunning || !leading;
			state = skip ? prevState : proto.isAction ? [depState[0]] : depState;
			const delay$1 = Math.max(0, Math.min(min, max - (now - start)));
			running.set(startsKey, start);
			ctx.schedule(() => running.delete(startsKey), -1);
			const timeoutId = setTimeout(() => ctx.get((read, acualize) => {
				if (read(proto) === ctx.cause) {
					running.delete(startsKey);
					if (trailing && skip) acualize(ctx, proto, (patchCtx, patch) => {
						patch.state = proto.isAction ? [depState.at(-1)] : depState;
					});
				}
			}), delay$1);
			timeoutId.unref?.();
			ctx.schedule(() => clearTimeout(timeoutId), -1);
			if (abortable) onCtxAbort(ctx, () => clearTimeout(timeoutId));
		}
		return state;
	}, name);
	const proto = theAtom.__reatom;
	proto.isAction = anAtom.__reatom.isAction;
	theAtom.deps = [anAtom];
	return theAtom;
};
const debounce = (min = 1, name) => (anAtom) => delay({
	min,
	leading: false,
	trailing: true
}, mapName(anAtom, "debounce", name))(anAtom);
const throttle = (max = 1, name) => (anAtom) => delay({
	max,
	leading: true,
	trailing: false
}, mapName(anAtom, "throttle", name))(anAtom);

//#endregion
//#region src/effect.ts
const effect = (fn, name) => (anAtom) => {
	const { isAction: isAction$1 } = anAtom.__reatom;
	const theAtom = atom((ctx, state = []) => {
		const resolve = (params, payload) => ctx.get((read, acualize) => {
			if (payload instanceof Promise) payload.then((payload$1) => resolve(params, payload$1));
			else acualize(ctx, ctx.cause.proto, (patchCtx, patch) => {
				patch.state = [{
					params,
					payload
				}];
			});
		});
		ctx.spy(anAtom, (value) => {
			if (isAction$1 && value.payload instanceof Promise) __thenReatomed(ctx, value.payload, (payload) => ctx.schedule(() => resolve([{
				params: value.params,
				payload
			}], fn(ctx, payload, value.params))));
			else ctx.schedule(() => resolve([value], isAction$1 ? fn(ctx, value.payload, value.params) : fn(ctx, value)));
		});
		ctx.spy = undefined;
		return state ?? [];
	}, mapName(anAtom, "effect", name));
	theAtom.__reatom.isAction = true;
	theAtom.deps = [anAtom];
	theAtom.unstable_unhook = onUpdate(theAtom, noop);
	return theAtom;
};

//#endregion
//#region src/filter.ts
const filter = (predicate, name) => (anAtom) => {
	const { isAction: isAction$1 } = anAtom.__reatom;
	predicate ??= isAction$1 ? () => true : (ctx, a, b) => !isShallowEqual(a, b);
	const theAtom = atom((ctx, prevState) => {
		const isInit = ctx.cause.pubs.length === 0;
		const state = ctx.spy(anAtom);
		return isAction$1 ? state.reduce((acc, call) => predicate(ctx, call.payload, call.params) ? [call] : acc, prevState ?? []) : isInit || predicate(ctx, state, prevState) ? state : prevState;
	}, mapName(anAtom, "filter", name));
	theAtom.deps = [anAtom];
	theAtom.__reatom.isAction = isAction$1;
	return theAtom;
};

//#endregion
//#region src/onLensUpdate.ts
const onLensUpdate = onUpdate;
const toLens = (deps) => (anAtom) => assign(anAtom, { deps: (anAtom.deps ?? []).concat(deps) });

//#endregion
//#region src/parseAtoms.ts
const parseAtoms = (ctx, value) => {
	if (isAction(value)) return value;
	if (isLinkedListAtom(value)) value = value.array;
	while (isAtom(value)) value = ctx.spy ? ctx.spy(value) : ctx.get(value);
	if (typeof value !== "object" || value === null) return value;
	if (isRec(value)) {
		const res = {};
		for (const k in value) res[k] = parseAtoms(ctx, value[k]);
		return res;
	}
	if (Array.isArray(value)) {
		const res = [];
		for (const v of value) res.push(parseAtoms(ctx, v));
		return res;
	}
	if (value instanceof Map) {
		const res = new Map();
		for (const [k, v] of value) res.set(k, parseAtoms(ctx, v));
		return res;
	}
	if (value instanceof Set) {
		const res = new Set();
		for (const v of value) res.add(parseAtoms(ctx, v));
		return res;
	}
	return value;
};

//#endregion
//#region src/sample.ts
const sample = (signal, name) => (anAtom) => {
	name = mapName(anAtom, "sample", name);
	const { isAction: isAction$1 } = anAtom.__reatom;
	const _cacheAtom = atom(null, `${name}._cacheAtom`);
	const theAtom = atom((ctx, prevState) => {
		const patch = ctx.cause;
		const isInit = patch.pubs.length === 0;
		ctx.spy(anAtom, (v) => _cacheAtom(ctx, isAction$1 ? [v] : v));
		let changed = false;
		ctx.spy(signal, () => changed = true);
		if (changed && !(isInit && !signal.__reatom.isAction)) {
			const state = ctx.get(_cacheAtom);
			_cacheAtom(ctx, ctx.get(anAtom));
			return state;
		}
		return isInit && !(signal.__reatom.isAction && changed) ? isAction$1 ? [] : ctx.get(anAtom) : changed ? ctx.get(_cacheAtom) : prevState;
	}, name);
	theAtom.__reatom.isAction = isAction$1;
	theAtom.deps = [anAtom, signal];
	return theAtom;
};

//#endregion
//#region src/select.ts
const mapAtom = atom(null, "select._map");
mapAtom.__reatom.initState = () => new WeakMap();
const touchedMap = new WeakMap();
const select = (ctx, cb, equal = () => false) => {
	let touched = touchedMap.get(ctx);
	if (!touched) touchedMap.set(ctx, touched = new Set());
	const map = ctx.get(mapAtom);
	let atoms = map.get(ctx.cause.proto);
	if (!atoms) map.set(ctx.cause.proto, atoms = new Map());
	const selectSource = cb.toString();
	throwReatomError(touched.has(selectSource), "multiple select with the same \"toString\" representation is not allowed");
	touched.add(selectSource);
	let selectAtom = atoms.get(selectSource);
	if (!selectAtom) {
		let isInit = true;
		atoms.set(selectSource, selectAtom = atom((ctx$1, prevState) => {
			const newState = cb(ctx$1);
			const resultState = isInit || !equal(prevState, newState) ? newState : prevState;
			isInit = false;
			return resultState;
		}, __count(`${ctx.cause.proto.name}._select`)));
	}
	return ctx.spy(selectAtom);
};

//#endregion
//#region src/withReset.ts
const withReset = () => (anAtom) => Object.assign(anAtom, { reset: action((ctx) => ctx.get((read, actualize) => actualize(ctx, anAtom.__reatom, (patchCtx, patch) => patch.state = patch.proto.initState(ctx)).state), `${anAtom.__reatom.name}._reset`) });

//#endregion
//#region src/index.ts
const combine = (shape, name = __count("_combine")) => {
	const theAtom = atom((ctx) => {
		const newState = {};
		for (const key in shape) newState[key] = ctx.spy(shape[key]);
		return newState;
	}, name);
	theAtom.deps = Object.values(shape);
	return theAtom;
};
const SKIP = "REATOM_SKIP_MARK";
const readonly = ({ __reatom, pipe, onChange, onCall, deps }) => Object.assign({
	__reatom,
	pipe,
	onChange
}, deps ? { deps } : {}, onCall ? { onCall } : {});
const plain = (anAtom) => {
	const theAtom = typeof anAtom === "function" ? anAtom.bind() : {};
	theAtom.__reatom = anAtom.__reatom;
	theAtom.pipe = anAtom.pipe;
	theAtom.onChange = anAtom.onChange;
	if ("onCall" in anAtom) theAtom.onCall = anAtom.onCall;
	return theAtom;
};
const mapState = (mapper, name) => (anAtom) => {
	const theAtom = atom((ctx, state) => mapper(ctx, ctx.spy(anAtom), ctx.cause.pubs.at(0)?.state, state), mapName(anAtom, "mapState", name));
	theAtom.deps = [anAtom];
	return theAtom;
};
const mapPayload = (fallbackOrMapper, mapOrName, name) => (anAction) => {
	throwReatomError(!anAction.__reatom.isAction, "action expected");
	const isAction$1 = typeof fallbackOrMapper === "function";
	let fallback = fallbackOrMapper;
	let map = mapOrName ?? ((ctx, v) => v);
	if (isAction$1) {
		fallback = [];
		map = fallbackOrMapper;
		name = mapOrName;
	}
	const theAtom = Object.assign(() => throwReatomError(1, "derived action call"), anAction.pipe(mapState((ctx, depState, prevDepState, prevState = fallback) => {
		return isAction$1 ? (ctx.spy = undefined, depState.reduce((acc, v) => {
			const payload = map(ctx, v.payload, v.params);
			return payload === SKIP ? acc : [...acc, {
				params: [v],
				payload
			}];
		}, prevState)) : depState.reduce((acc, { payload, params }) => {
			const state = map(ctx, payload, params, acc);
			return state === SKIP ? acc : state;
		}, prevState);
	}, mapName(anAction, "mapPayload", name))));
	theAtom.__reatom.isAction = isAction$1;
	return theAtom;
};
const mapPayloadAwaited = (...a) => (anAction) => {
	const isAction$1 = a.length === 0 || typeof a[0] === "function";
	const [fallback, map = (ctx, v) => v, name] = isAction$1 ? [
		[],
		a[0],
		a[1]
	] : a;
	const params = isAction$1 ? [] : [fallback];
	params.push((ctx, promise) => {
		if (promise instanceof Promise) {
			__thenReatomed(ctx, promise, (v, read, actualize) => actualize(ctx, ctx.cause.proto, (patchCtx, patch) => {
				patch.cause = ctx.cause.cause;
				const payload = map(ctx, v);
				patch.state = isAction$1 ? [...patch.state, {
					params: [v],
					payload
				}] : payload;
			}));
			return SKIP;
		} else return map(ctx, promise);
	}, mapName(anAction, "mapPayloadAwaited", name));
	return (anAction.onFulfill ?? anAction).pipe(mapPayload(
		// @ts-ignore
		...params
));
};
const mapInput = (mapper, name) => (anAtom) => action((ctx, ...args) => anAtom(
	ctx,
	// @ts-ignore
	mapper(ctx, ...args)
), mapName(anAtom, "mapInput", name));
const toAtom = (fallback, name) => (anAction) => mapPayload(fallback, (ctx, v) => v, mapName(anAction, "toAtom", name))(anAction);
const withOnUpdate = (cb) => (anAtom) => {
	onUpdate(anAtom, cb);
	return anAtom;
};
const toAction = (map, name) => (anAtom) => {
	throwReatomError(anAtom.__reatom.isAction, "atom expected");
	if (typeof map === "string") {
		name = map;
		map = undefined;
	}
	map ??= (ctx, v) => v;
	const theAction = atom((ctx) => {
		const isInit = ctx.cause.pubs.length === 0;
		const state = ctx.spy(anAtom);
		return isInit ? [] : [{
			params: [state],
			payload: map(ctx, state)
		}];
	}, mapName(anAtom, "toAction", name));
	theAction.__reatom.isAction = true;
	theAction.deps = [anAtom];
	return theAction;
};

//#endregion
export { SKIP, bind, combine, debounce, delay, effect, filter, mapInput, mapPayload, mapPayloadAwaited, mapState, match, onLensUpdate, parseAtoms, plain, readonly, sample, select, throttle, toAction, toAtom, toLens, withOnUpdate, withReset };